import React, { useEffect, useState } from "react";
import { fetchSaques } from "../../../integration/saque/historico_saques/fetchSaques";
import { fetchConfirmarSaques } from "../../../integration/saque/confirmar_saque/fetchConfirmarSaques";
import { fetchChavePix } from "../../../integration/pix/fetchChavePix";
import { formatToBRL } from "brazilian-values";
import { CheckCircle, Clock } from "lucide-react";
import { BiMoneyWithdraw } from "react-icons/bi";
import { fetchSaldo } from "../../../integration/saldo/fetchSaldo";
import { fetchTaxa } from "../../../integration/taxas/fetchTaxa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SaquesTabela = () => {
  const [clientes, setClientes] = useState([]);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const itensPorPagina = 10;

  const funcListaSaques = async () => {
    setCarregando(true);
    try {
      const resposta = await fetchSaques({ paginaAtual, itensPorPagina });
      const { data, total, page, pages } = resposta;

      setClientes(data || []);
      setTotalRegistros(total || 0);
      setTotalPaginas(pages || 1);
      setPaginaAtual(page || 1);
    } catch (erro) {
      console.error("Erro ao buscar saques:", erro);
      setClientes([]);
      setTotalRegistros(0);
      setTotalPaginas(1);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    funcListaSaques();
  }, [paginaAtual]);

  const statusSaque = {
    waiting_withdraw: "Pendente",
    finish: "Finalizado"
  };

  useEffect(() => {
    const salvo = localStorage.getItem("ultimoSaque");
    if (salvo) {
      setUltimoSaque(new Date(salvo));
    }
  }, []);

  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [valorSaque, setValorSaque] = useState("");
  const [chaveSelecionada, setChaveSelecionada] = useState("");
  const [ultimoSaque, setUltimoSaque] = useState(null);
  const [disabled, setDisabled] = useState(false);

  const handleSolicitarSaque = async () => {
    const agora = new Date();
    const ultimoSaqueLocal = localStorage.getItem("ultimoSaque");
    const ultimoSaqueDate = ultimoSaqueLocal
      ? new Date(ultimoSaqueLocal)
      : null;

    if (ultimoSaqueDate && agora - ultimoSaqueDate < 20000) {
      toast.warning("Aguarde 1 minuto antes de solicitar outro saque.");
      return;
    }

    if (disabled) return;

    setDisabled(true);
    setTimeout(() => setDisabled(false), 60 * 1000);

    try {
      await fetchConfirmarSaques({
        valorSaque,
        chaveSelecionada
      });

      toast.success("Saque realizado com sucesso!");
      setValorSaque("");
      setChaveSelecionada("");
      funcListaSaques();
      setErrorMessage("");

      localStorage.setItem("ultimoSaque", agora.toISOString());
      setUltimoSaque(agora);
    } catch (erro) {
      const msgErro =
        erro.response?.data?.error ||
        erro?.error ||
        erro.message ||
        "Erro de servidor";

      toast.error(msgErro);
    }
  };

  useEffect(() => {
    funcSaldo();
    funcTaxa();
    carregarChaves();
  }, []);

  const [chavesPix, setChavesPix] = useState([]);

  const carregarChaves = async () => {
    setCarregando(true);
    try {
      const resposta = await fetchChavePix();
      setChavesPix(resposta.data);
    } catch (erro) {
      setChavesPix([]);
      const msgErro =
        erro.response?.data?.error ||
        erro?.error ||
        erro.message ||
        "Erro de servidor";

      toast.error(msgErro);
    } finally {
      setCarregando(false);
    }
  };

  const [saldo, setSaldo] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  async function funcSaldo() {
    try {
      const resp = await fetchSaldo();
      setSaldo(resp.balance);
      setErrorMessage("");
    } catch (erro) {
      setErrorMessage(erro);
    }
  }

  const [taxas, setTaxas] = useState({
    pix_porcent: null,
    pix_fix: null,
    pix_reserv: null,
    withdrawalFixed: null,
    saque_porcent: null
  });

  const funcTaxa = async () => {
    try {
      const resposta = await fetchTaxa();
      setTaxas(resposta);
    } catch (erro) {
      console.error("Erro ao buscar histórico de taxa:", erro);
    }
  };

  const calcularValorLiquido = () => {
    const valorCentavos = parseInt(valorSaque || "0", 10);
    const valorReais = valorCentavos / 100;
    const taxaFixa = (taxas?.withdrawalFixed || 0) / 100;
    const liquido = valorReais - taxaFixa;
    return formatToBRL(liquido > 0 ? liquido : 0);
  };

  const handleChange = (e) => {
    const somenteNumeros = e.target.value.replace(/\D/g, "");
    setValorSaque(somenteNumeros);
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-6 bg-[#fcfcfd] rounded-2xl">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="mb-6">
        <button
          onClick={() => setMostrarFormulario(!mostrarFormulario)}
          className="inline-flex items-center gap-2 px-5 py-2 bg-[#f5f4f1] text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-100 transition"
        >
          <BiMoneyWithdraw className="w-5 h-5" />
          Solicitar Saque
        </button>

        {mostrarFormulario && (
          <div className="mt-4 p-6 bg-white rounded-xl border border-gray-200 shadow-sm space-y-4">
            <h2 className="text-3xl font-light text-[#0c0c0c]">
              Informações de Saques
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6 text-sm text-gray-700">
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <p className="font-semibold">Saldo disponível</p>
                <p className="text-gray-500">{formatToBRL(saldo / 100 || 0)}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <p className="font-semibold">Taxa de Saque</p>
                <p className="text-gray-500">
                  {taxas.withdrawalFixed
                    ? `${formatToBRL(taxas.withdrawalFixed / 100)}`
                    : "Não disponível"}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Valor do Saque
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formatToBRL(valorSaque / 100 || 0)}
                onChange={handleChange}
                className="border p-2 rounded w-full text-gray-700"
                placeholder="R$ 0,00"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Selecione sua chave Pix
              </label>
              <select
                value={chaveSelecionada}
                onChange={(e) => setChaveSelecionada(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="">Selecione...</option>
                {chavesPix.map((chaveObj) => (
                  <option key={chaveObj.id} value={chaveObj.key}>
                    {`${chaveObj.key} (${chaveObj.type})`}
                  </option>
                ))}
              </select>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="font-semibold">Valor líquido</p>
              <p className="text-green-500 font-bold">
                {calcularValorLiquido()}
              </p>
            </div>

            <div className="text-right">
              <button
                onClick={handleSolicitarSaque}
                disabled={disabled}
                className={`inline-flex items-center gap-2 px-5 py-2 font-medium rounded-lg border transition
             ${
               disabled
                 ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                 : "bg-[#f5f4f1] text-gray-700 border-gray-300 hover:bg-gray-100"
             }
           `}
              >
                {disabled ? "Aguarde 1 minuto..." : "Confirmar Saque"}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-3xl font-light text-[#0c0c0c]">
          Histórico de Saques
        </h2>
        <span className="text-gray-500 text-sm">
          Total: <strong>{totalRegistros}</strong> registros
        </span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm text-gray-700">
          <thead className="bg-[#f5f4f1] text-gray-700 font-semibold">
            <tr>
              <th className="px-4 py-3 text-left">Criado em</th>
              <th className="px-4 py-3 text-left">Valor</th>
              <th className="px-4 py-3 text-left">Chave</th>
              <th className="px-4 py-3 text-left">Status</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-100">
            {carregando ? (
              <tr>
                <td colSpan={8} className="text-center py-4 text-gray-500">
                  Carregando...
                </td>
              </tr>
            ) : clientes.length > 0 ? (
              clientes.map((cliente, index) => (
                <tr
                  key={cliente.key || index}
                  className="hover:bg-gray-50 transition"
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    {new Date(cliente.date).toLocaleString("pt-BR")}
                  </td>
                  <td className="px-4 py-3">
                    {formatToBRL(cliente.amount / 100 || 0)}
                  </td>
                  <td className="px-4 py-3">{cliente.key}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                        cliente.status === "Finalizado"
                          ? "bg-green-100 text-green-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {cliente.status === "Finalizado" ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <Clock className="w-4 h-4" />
                      )}
                      {statusSaque[cliente.status] || cliente.status}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="text-center py-4 text-gray-500">
                  Nenhum saque encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ✅ PAGINAÇÃO ABAIXO DA TABELA */}
      <div className="mt-6 flex justify-between items-center text-sm text-gray-700">
        <div>
          Página <strong>{paginaAtual}</strong> de{" "}
          <strong>{totalPaginas}</strong>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPaginaAtual(Math.max(1, paginaAtual - 1))}
            disabled={paginaAtual === 1}
            className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 transition disabled:opacity-50"
          >
            Anterior
          </button>
          <button
            onClick={() =>
              setPaginaAtual(Math.min(totalPaginas, paginaAtual + 1))
            }
            disabled={paginaAtual === totalPaginas}
            className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 transition disabled:opacity-50"
          >
            Próxima
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaquesTabela;
